import type { ReactElement, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import type {
  AggregatedPackage,
  AggregatedRepository,
  AgentCommandEvent,
  AgentCommandEventType,
} from '@kirin/api-client/contracts/agent-command-events';
import type { FilterValue } from '@kirin/api-client/contracts/table';
import {
  buildPackageGroupFilters,
  buildRepositoryGroupFilters,
  fetchGroupEvents,
  groupEventsQueryKey,
} from './api';

const DASH = '—';
const RECENT_COMMANDS_LIMIT = 10;
const OCCURRENCES_LIMIT = 100;

function text(value: string | null): string {
  return value && value.trim() ? value : DASH;
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

interface IdentityRow {
  label: string;
  value: string | null;
}

function Row({ label, value }: IdentityRow): ReactElement | null {
  if (!value || !value.trim()) return null;
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="break-all">{value}</span>
    </div>
  );
}

/** Shared "raw commands (N most recent)" block for a group. */
function RawCommandsList({
  events,
  isLoading,
}: {
  events: AgentCommandEvent[];
  isLoading: boolean;
}): ReactElement {
  if (isLoading) {
    return <p className="mt-1 text-xs text-muted-foreground">Loading…</p>;
  }
  if (events.length === 0) {
    return (
      <p className="mt-1 text-xs text-muted-foreground">
        No commands recorded.
      </p>
    );
  }
  return (
    <div className="mt-1 flex flex-col gap-2">
      {events.map(event => (
        <pre
          key={event.id}
          className="max-w-full whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-xs"
        >
          {event.rawCommand}
        </pre>
      ))}
    </div>
  );
}

/** Shared unaggregated-occurrences table (agent / user / command / timestamp). */
function OccurrencesTable({
  events,
  isLoading,
}: {
  events: AgentCommandEvent[];
  isLoading: boolean;
}): ReactElement {
  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Loading…</p>;
  }
  return (
    <table className="w-full text-left text-sm">
      <thead className="text-muted-foreground">
        <tr>
          <th className="py-2 pr-4 font-medium">Agent</th>
          <th className="py-2 pr-4 font-medium">User</th>
          <th className="py-2 pr-4 font-medium">Command</th>
          <th className="py-2 font-medium">Timestamp</th>
        </tr>
      </thead>
      <tbody>
        {events.map(event => (
          <tr key={event.id} className="border-t align-top">
            <td className="py-2 pr-4">{event.agentName}</td>
            <td className="py-2 pr-4">{text(event.userEmail)}</td>
            <td className="py-2 pr-4">
              <code className="break-all text-xs">{event.rawCommand}</code>
            </td>
            <td className="py-2 whitespace-nowrap">
              {formatTimestamp(event.timestamp)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Fetch a group's unaggregated events; disabled until `open`. */
function useGroupEvents(
  open: boolean,
  organizationId: string | undefined,
  eventType: AgentCommandEventType,
  filters: FilterValue[],
  limit: number
): { events: AgentCommandEvent[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: open
      ? groupEventsQueryKey(organizationId, eventType, filters, { limit })
      : ['group-events', 'idle'],
    queryFn: () =>
      fetchGroupEvents(organizationId, eventType, filters, { limit }),
    enabled: open,
  });
  return { events: data?.events ?? [], isLoading: open && isLoading };
}

/**
 * Generic row-click detail modal for an aggregated group (KIR-3807): identity
 * rows + the 10 most recent raw commands (lazy-fetched on open). Shared by the
 * package and repository views.
 */
function GroupDetailDialog({
  open,
  title,
  subtitle,
  knownBad,
  identityRows,
  organizationId,
  eventType,
  filters,
  onClose,
}: {
  open: boolean;
  title: string;
  subtitle: string;
  knownBad: boolean;
  identityRows: IdentityRow[];
  organizationId: string | undefined;
  eventType: AgentCommandEventType;
  filters: FilterValue[];
  onClose: () => void;
}): ReactElement {
  const { events, isLoading } = useGroupEvents(
    open,
    organizationId,
    eventType,
    filters,
    RECENT_COMMANDS_LIMIT
  );
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
            {knownBad && (
              <Badge variant="destructive" className="uppercase">
                Known bad
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>
        {open && (
          <div className="flex min-w-0 flex-col gap-2">
            {identityRows.map(r => (
              <Row key={r.label} label={r.label} value={r.value} />
            ))}
            <div className="mt-2">
              <span className="text-sm text-muted-foreground">
                Raw commands (10 most recent)
              </span>
              <RawCommandsList events={events} isLoading={isLoading} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Generic occurrences drill-down (unaggregated events for one group). */
function GroupOccurrencesDialog({
  open,
  subtitle,
  organizationId,
  eventType,
  filters,
  onClose,
}: {
  open: boolean;
  subtitle: ReactNode;
  organizationId: string | undefined;
  eventType: AgentCommandEventType;
  filters: FilterValue[];
  onClose: () => void;
}): ReactElement {
  const { events, isLoading } = useGroupEvents(
    open,
    organizationId,
    eventType,
    filters,
    OCCURRENCES_LIMIT
  );
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Occurrences</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          <OccurrencesTable events={events} isLoading={isLoading} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Package wrappers ────────────────────────────────────────────────────────

export function AggregatedPackageDetailDialog({
  organizationId,
  pkg,
  onClose,
}: {
  organizationId: string | undefined;
  pkg: AggregatedPackage | null;
  onClose: () => void;
}): ReactElement {
  return (
    <GroupDetailDialog
      open={!!pkg}
      title="Package install"
      subtitle={`Aggregated across ${pkg?.occurrenceCount ?? 0} occurrences.`}
      knownBad={pkg?.knownBad ?? false}
      identityRows={[
        { label: 'Package', value: pkg?.packageName ?? null },
        { label: 'Version', value: pkg?.packageVersion ?? null },
        { label: 'Manager', value: pkg?.packageManager ?? null },
        { label: 'Source', value: pkg?.sourceUrl ?? null },
        { label: 'Project', value: pkg?.projectPath ?? null },
        { label: 'Users', value: pkg ? String(pkg.userCount) : null },
        {
          label: 'Occurrences',
          value: pkg ? String(pkg.occurrenceCount) : null,
        },
        {
          label: 'Last seen',
          value: pkg ? new Date(pkg.lastSeen).toLocaleString() : null,
        },
      ]}
      organizationId={organizationId}
      eventType="package"
      filters={pkg ? buildPackageGroupFilters(pkg) : []}
      onClose={onClose}
    />
  );
}

AggregatedPackageDetailDialog.displayName = 'AggregatedPackageDetailDialog';

export function PackageOccurrencesDialog({
  organizationId,
  pkg,
  onClose,
}: {
  organizationId: string | undefined;
  pkg: AggregatedPackage | null;
  onClose: () => void;
}): ReactElement {
  return (
    <GroupOccurrencesDialog
      open={!!pkg}
      subtitle={
        <>
          {pkg?.packageName}
          {pkg?.packageVersion ? `@${pkg.packageVersion}` : ''} — most recent{' '}
          {OCCURRENCES_LIMIT} installs.
        </>
      }
      organizationId={organizationId}
      eventType="package"
      filters={pkg ? buildPackageGroupFilters(pkg) : []}
      onClose={onClose}
    />
  );
}

PackageOccurrencesDialog.displayName = 'PackageOccurrencesDialog';

// ── Repository wrappers ─────────────────────────────────────────────────────

export function AggregatedRepositoryDetailDialog({
  organizationId,
  repo,
  onClose,
}: {
  organizationId: string | undefined;
  repo: AggregatedRepository | null;
  onClose: () => void;
}): ReactElement {
  return (
    <GroupDetailDialog
      open={!!repo}
      title="Repository access"
      subtitle={`Aggregated across ${repo?.occurrenceCount ?? 0} occurrences.`}
      knownBad={repo?.knownBad ?? false}
      identityRows={[
        { label: 'Repository', value: repo?.repoUrl ?? null },
        { label: 'Action', value: repo?.action ?? null },
        { label: 'Branch', value: repo?.branch ?? null },
        { label: 'Project', value: repo?.projectPath ?? null },
        { label: 'Users', value: repo ? String(repo.userCount) : null },
        {
          label: 'Occurrences',
          value: repo ? String(repo.occurrenceCount) : null,
        },
        {
          label: 'Last seen',
          value: repo ? new Date(repo.lastSeen).toLocaleString() : null,
        },
      ]}
      organizationId={organizationId}
      eventType="git"
      filters={repo ? buildRepositoryGroupFilters(repo) : []}
      onClose={onClose}
    />
  );
}

AggregatedRepositoryDetailDialog.displayName =
  'AggregatedRepositoryDetailDialog';

export function RepositoryOccurrencesDialog({
  organizationId,
  repo,
  onClose,
}: {
  organizationId: string | undefined;
  repo: AggregatedRepository | null;
  onClose: () => void;
}): ReactElement {
  return (
    <GroupOccurrencesDialog
      open={!!repo}
      subtitle={
        <>
          {repo?.repoUrl}
          {repo?.branch ? ` (${repo.branch})` : ''} — most recent{' '}
          {OCCURRENCES_LIMIT} accesses.
        </>
      }
      organizationId={organizationId}
      eventType="git"
      filters={repo ? buildRepositoryGroupFilters(repo) : []}
      onClose={onClose}
    />
  );
}

RepositoryOccurrencesDialog.displayName = 'RepositoryOccurrencesDialog';
